import { ForwardedRef } from 'react';

import { ContentType } from '@/common/constants/ContentTypes';

import { CommentSection } from '@/components/Comments';
import { ContentSection } from '@/domains/blogs/components/ContentSection/ContentSection';
import { SectionNames } from '@/domains/blogs/constants/SectionNames';
import { DiscussionTitle } from '@/domains/discussions/styles';
import { Variant } from '@/types/Content/Product/Variant';
import { BlogContentType } from '@/domains/blogs/types';
import { ProductCarousel } from '../components/ProductCarousel';
import { TLDR } from '../components/TLDR';
import { VideoSection } from '../components/VideoSection';
import { SocialShareDesktop } from '../BlogPostPage.styles';
import { PurchaseSection } from '../components/PurchaseSection/PurchaseSection';
import RecommendedGroupsSection from '../components/RecommendedGroupsSection';
import { BannerSection } from '../components/BannerSection/BannerSection';

interface ISectionByName {
  contentType: ContentType;
  sectionName: SectionNames;
  content: BlogContentType;
  currentVariant?: Variant;
  commentsRef?: ForwardedRef<HTMLDivElement>;
  toCommentsClick?: () => void;
}

export const SectionByName: React.FC<ISectionByName> = ({
  content,
  contentType,
  sectionName,
  currentVariant,
  commentsRef,
  toCommentsClick,
}) => {
  const {
    id,
    seo,
    body,
    tldr,
    title,
    image,
    video,
    images,
    vendor,
    author,
    options,
    comments,
    variants,
    createdAt,
    updatedAt,
    shopifyId,
    resourceType,
    relatedGroups,
    affiliatedLinks,
  } = content;

  switch (sectionName) {
    case SectionNames.BODY:
      return <ContentSection content={content} contentType={contentType} />;

    case SectionNames.COMMENTS:
      return (
        <CommentSection contentId={id} comments={comments} ref={commentsRef} />
      );

    case SectionNames.RELATED_GROUPS:
      return <RecommendedGroupsSection groups={relatedGroups} />;

    case SectionNames.IMAGE_TITLE:
      return (
        <BannerSection
          image={image}
          title={title}
          iconUrl={resourceType.iconUrl}
        />
      );

    case SectionNames.TITLE:
      return <DiscussionTitle>{title}</DiscussionTitle>;

    case SectionNames.PRODUCT_CAROUSEL:
      return (
        <ProductCarousel
          title={title}
          seller={vendor?.name}
          images={images || [image]}
        />
      );

    case SectionNames.VIDEO:
      return (
        <VideoSection
          hosting={video.hosting}
          videoId={video.videoId}
          title={title}
        />
      );

    case SectionNames.TLDR:
      return (
        <TLDR
          id={id}
          body={body}
          tldr={tldr}
          author={author}
          createdAt={createdAt}
          updatedAt={updatedAt}
          contentType={contentType}
        />
      );

    case SectionNames.SOCIAL_MEDIA:
      return (
        <SocialShareDesktop
          seo={seo}
          title={title}
          commentCounter={comments?.length}
          toCommentsClick={toCommentsClick}
        />
      );

    case SectionNames.PURCHASE:
      return (
        <PurchaseSection
          id={id}
          seo={seo}
          type={content?.contentType}
          title={title}
          options={options}
          variants={variants}
          shopifyId={shopifyId}
          currentVariant={currentVariant}
          affiliatedLinks={affiliatedLinks}
          commentCounter={comments?.length}
          toCommentsClick={toCommentsClick}
        />
      );

    default:
      return null;
  }
};

SectionByName.defaultProps = {
  currentVariant: null,
};
